import {
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type RefObject,
} from "react";

const intersectionObserverOptions: IntersectionObserverInit = {
	root: null, // use viewport
	rootMargin: "0px",
	threshold: 0,
};

const useElementOnScreen = <T extends Element = HTMLElement>(
	options: IntersectionObserverInit = intersectionObserverOptions,
): [RefObject<T | null>, boolean] => {
	const [isVisible, setIsVisible] = useState(false);
	const containerRef = useRef<T | null>(null);
	const updatedOptions = useMemo(
		() => ({ ...intersectionObserverOptions, ...options }),
		[options],
	);

	useLayoutEffect(() => {
		const currentElement = containerRef.current;
		if (!currentElement) return;

		const observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting) {
				setIsVisible(entry.isIntersecting);
				observer.disconnect();
			}
		}, updatedOptions);

		observer.observe(currentElement);

		return () => {
			observer.disconnect();
		};
	}, [updatedOptions]);

	return [containerRef, isVisible];
};

export default useElementOnScreen;
